import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { Business, SetupIntentResponse, BusinessPaymentMethod } from '../../models/Business';
import { AuthService } from '../auth/auth.service';
import { OrgContextStorageService } from '../auth/org-context-storage.service';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class BusinessService {
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly orgContextStorage = inject(OrgContextStorageService);
  private get baseApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/businesses`); }

  private readonly _currentBusiness = signal<Business | null>(null);
  readonly currentBusiness: Signal<Business | null> = this._currentBusiness.asReadonly();
  readonly business$ = toObservable(this._currentBusiness);

  constructor(
    private httpClient: HttpClientWrapperService,
    private authService: AuthService
  ) {
    this.initializeBusiness();
    this.setupUserListener();
  }

  private initializeBusiness(): void {
    const businessId: string | null = sessionStorage.getItem('currentBusinessId');
    if (businessId) {
      this.setCurrentBusiness(businessId, false);
    }
  }

  private setupUserListener(): void {
    // user$ replays its current value (null) synchronously at boot; clearing on
    // that initial emission deletes the sessionStorage key initializeBusiness()
    // just restored. Only clear when a logged-in user actually logs out.
    let hasSeenUser: boolean = false;
    this.authService.user$.subscribe((user) => {
      if (user) {
        hasSeenUser = true;
        return;
      }
      if (hasSeenUser) {
        hasSeenUser = false;
        this.clearCurrentBusiness();
      }
    });
  }

  setCurrentBusiness(id: string, persist: boolean = true): void {
    this.httpClient.get<Business>(`${this.baseApiUrl}/${id}`).subscribe({
      next: (res: Business) => {
        this._currentBusiness.set(res);
        if (persist) {
          sessionStorage.setItem('currentBusinessId', id);
          try { this.orgContextStorage.setLastUsedOrgId('business', id); } catch { /* persistence is best-effort */ }
        }
      },
      error: () => {
        this.clearCurrentBusiness();
      }
    });
  }

  clearCurrentBusiness(): void {
    this._currentBusiness.set(null);
    sessionStorage.removeItem('currentBusinessId');
  }

  getBusiness(id: string): Observable<Business> {
    return this.httpClient.get<Business>(`${this.baseApiUrl}/${id}`);
  }

  updateBusiness(id: string, business: Business): Observable<Business> {
    return this.httpClient.put<Business, Business>(`${this.baseApiUrl}/${id}`, business);
  }

  changeBusinessProfilePic(id: number, formData: FormData): Observable<Business> {
    return this.httpClient.post<Business, FormData>(
      `${this.baseApiUrl}/${id}/upload-image`,
      formData,
    );
  }

  generateLinkCode(id: number): Observable<string> {
    return this.httpClient.postText(`${this.baseApiUrl}/${id}/generate-link-code`, null);
  }

  createSetupIntent(businessId: number): Observable<SetupIntentResponse> {
    return this.httpClient.post<SetupIntentResponse, null>(
      `${this.baseApiUrl}/${businessId}/setup-intent`,
      null
    );
  }

  savePaymentMethod(businessId: number, paymentMethodId: string): Observable<BusinessPaymentMethod> {
    return this.httpClient.post<BusinessPaymentMethod, { paymentMethodId: string }>(
      `${this.baseApiUrl}/${businessId}/payment-method`,
      { paymentMethodId }
    );
  }

  getPaymentMethod(businessId: number): Observable<BusinessPaymentMethod> {
    return this.httpClient.get<BusinessPaymentMethod>(
      `${this.baseApiUrl}/${businessId}/payment-method`
    );
  }

  removePaymentMethod(businessId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.baseApiUrl}/${businessId}/payment-method`
    );
  }

  refreshCurrentBusiness(): void {
    const businessId: string | null = sessionStorage.getItem('currentBusinessId');
    if (businessId) {
      this.setCurrentBusiness(businessId, false);
    }
  }
}
